'use client';

import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { useAuth } from '@/lib/auth/context';
import { displayAuthEmail, displayAuthPhone } from '@/lib/demo-privacy';
import { useTwaContext } from '@/lib/twa-detection';

interface NavItem {
  href: string;
  label: string;
  /** Colored-tile background for mini mode (Lex Meridian signature). */
  tile: string;
  icon: React.ReactNode;
  matchPrefix?: boolean;
  disabled?: boolean;
  disabledTitle?: string;
}

/* ───────────── SVG icons (single-weight line) ───────────── */
const IconHome = (
  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 10l8-7 8 7v10a1 1 0 01-1 1h-4v-6h-6v6H5a1 1 0 01-1-1z" />
  </svg>
);
const IconPlus = (
  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
  </svg>
);
const IconPlan = (
  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.5 7A2.5 2.5 0 016 4.5h12A2.5 2.5 0 0120.5 7v2H3.5V7z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.5 11h17v6A2.5 2.5 0 0118 19.5H6A2.5 2.5 0 013.5 17v-6z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 15.5h4" />
  </svg>
);
const IconPlay = (
  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <circle cx="12" cy="12" r="8.5" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 8.8v6.4L15.2 12 10 8.8z" />
  </svg>
);
const IconDemoCases = (
  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 4.5h7l3 3v12H7a2 2 0 01-2-2v-11a2 2 0 012-2z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M14 4.5v3h3M9 11h6M9 14h6M9 17h3" />
  </svg>
);
const IconBriefcase = (
  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6V5a2 2 0 012-2h0a2 2 0 012 2v1m-9 0h14a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18" />
  </svg>
);
const IconUpload = (
  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15V4m0 0l-4 4m4-4l4 4" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 15v3a2 2 0 002 2h12a2 2 0 002-2v-3" />
  </svg>
);
const IconCalendar = (
  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <rect x="3.5" y="5" width="17" height="15" rx="1.5" /><path strokeLinecap="round" strokeLinejoin="round" d="M8 3v4M16 3v4M3.5 10h17" />
  </svg>
);
const IconChart = (
  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 19V5M4 19h16" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16v-5M12 16V8M16 16v-7" />
  </svg>
);
const IconTag = (
  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
  </svg>
);
const IconDiscover = (
  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 20l-4.2-4.2" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 7.5v6M7.5 10.5h6" />
  </svg>
);
const IconCog = (
  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <circle cx="12" cy="12" r="3" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h.01a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v.01a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);
const IconUsers = (
  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 11a4 4 0 10-8 0" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 20a7.5 7.5 0 0115 0" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 8.5a3 3 0 010 6" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.5 20a5.5 5.5 0 00-3.2-5" />
  </svg>
);
const IconGift = (
  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 10h16v10H4V10zM3 7h18v3H3V7zM12 7v13M12 7H8.5a2.5 2.5 0 110-5C11 2 12 7 12 7zM12 7h3.5a2.5 2.5 0 100-5C13 2 12 7 12 7z" />
  </svg>
);
const IconSearch = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
    <circle cx="11" cy="11" r="6.5" /><path d="M20 20l-3.8-3.8" />
  </svg>
);
const IconCollapse = (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 6l-6 6 6 6" />
  </svg>
);
const IconLogout = (
  <svg className="w-[15px] h-[15px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

const WORKSPACE_ITEMS: NavItem[] = [
  { href: '/dashboard', label: 'Dashboard', tile: '#b33939', icon: IconHome },
  { href: '/demo-cases', label: 'Demo Cases', tile: '#596b8d', icon: IconDemoCases },
  { href: '/dashboard/watch-demo-video', label: 'Watch Demo Video', tile: '#7a5b2d', icon: IconPlay },
  { href: '/practice', label: 'Practice', tile: '#4b6f44', icon: IconBriefcase },
  { href: '/add-case', label: 'Add Case', tile: '#2f6b7a', icon: IconPlus },
  { href: '/bulk-add-cases', label: 'Bulk Add', tile: '#7a5b2d', icon: IconUpload },
  { href: '/calendar', label: 'Calendar', tile: '#2d4f7a', icon: IconCalendar },
  { href: '/discovery', label: 'Discovery', tile: '#6b4f7a', icon: IconDiscover },
  { href: '/analytics', label: 'Analytics', tile: '#4b6f44', icon: IconChart },
  { href: '/referrals', label: 'Refer & Earn', tile: '#a8676a', icon: IconGift },
  { href: '/settings/labels', label: 'Labels', tile: '#a8676a', icon: IconTag },
];

const PLANS_ITEM: NavItem = { href: '/pricing', label: 'Plans', tile: '#2f6b7a', icon: IconPlan };

const FIRM_ITEMS: NavItem[] = [
  { href: '/team', label: 'Users', tile: '#596b8d', icon: IconUsers },
  { href: '/settings', label: 'Settings', tile: '#5a6070', icon: IconCog },
];

const ACCOUNT_ITEMS: NavItem[] = [
  { href: '/settings', label: 'Settings', tile: '#5a6070', icon: IconCog },
];

type WorkspaceNavState = {
  sectionLabel: string;
  roleLabel: string;
  showAdvancedWorkspaceItems: boolean;
  showPracticeItem: boolean;
  showAddCaseItem: boolean;
  showBulkAddItem: boolean;
  showPlansItem: boolean;
};

export interface AppSidebarProps {
  className?: string;
  collapsed: boolean;
  onToggle: () => void;
  onOpenCmd: () => void;
  workspaceNav: WorkspaceNavState;
}

export default function AppSidebar({ className = '', collapsed, onToggle, onOpenCmd, workspaceNav }: AppSidebarProps) {
  const pathname = usePathname();
  const { user, signOut } = useAuth();
  const [hovered, setHovered] = useState<string | null>(null);
  // Google Play billing gate: never surface the Plans nav inside the Android app.
  const isTwa = useTwaContext();
  const showPlansItem = workspaceNav.showPlansItem && !isTwa;

  const isActive = (item: NavItem) => {
    if (item.href === '/settings' && pathname === '/settings/labels') return false;
    if (item.matchPrefix) return pathname.startsWith(item.href);
    return pathname === item.href;
  };

  const handleLogout = async () => {
    await signOut();
    window.location.href = '/';
  };

  const safeEmail = displayAuthEmail(user?.email);
  const safePhone = displayAuthPhone(user);
  const userLabel = safeEmail ?? safePhone ?? 'Account';
  const avatarLetter = (safeEmail?.[0] ?? safePhone?.[0] ?? 'A').toUpperCase();
  const showLockedFeatureItems = !workspaceNav.showAddCaseItem && workspaceNav.showPlansItem;
  const baseWorkspaceItems = workspaceNav.showAdvancedWorkspaceItems
    ? WORKSPACE_ITEMS
    : WORKSPACE_ITEMS.filter((item) => (
      item.href === '/dashboard' ||
      item.href === '/demo-cases' ||
      item.href === '/dashboard/watch-demo-video' ||
      item.href === '/add-case' ||
      item.href === '/bulk-add-cases' ||
      item.href === '/discovery' ||
      item.href === '/referrals' ||
      (showLockedFeatureItems && item.href === '/calendar')
    ));
  const workspaceFeatureItems = baseWorkspaceItems.flatMap((item) => {
    if (item.href === '/practice' && !workspaceNav.showPracticeItem) return [];
    if (item.href === '/bulk-add-cases' && !workspaceNav.showBulkAddItem) {
      return showLockedFeatureItems
        ? [{ ...item, disabled: true, disabledTitle: 'Subscribe to bulk add cases' }]
        : [];
    }
    if (item.href !== '/add-case') return [item];
    if (workspaceNav.showAddCaseItem) return [item];
    return showLockedFeatureItems
      ? [{ ...item, disabled: true, disabledTitle: 'Your free case slot is already in use' }]
      : [];
  });
  const workspaceItems = showPlansItem
    ? [...workspaceFeatureItems, PLANS_ITEM]
    : workspaceFeatureItems;
  const accountItems = workspaceNav.sectionLabel === 'Firm' || workspaceNav.sectionLabel === 'Corporate'
    ? FIRM_ITEMS
    : ACCOUNT_ITEMS;

  if (collapsed) {
    const allItems = [...workspaceItems, ...accountItems];
    return (
      <aside className={`ics-side ics-side-mini ${className}`}>
        <button
          type="button"
          className="brand-tile"
          onClick={onToggle}
          title="Expand sidebar"
          aria-label="Expand sidebar"
        >
          <Image src="/logo.png" alt="" width={22} height={22} className="rounded" />
        </button>

        <div className="mini-nav">
          {allItems.map((it) => {
            const active = isActive(it);
            return (
              <div key={it.href} className="relative">
                {it.disabled ? (
                  <button
                    type="button"
                    disabled
                    className="nav-mini cursor-not-allowed opacity-45 grayscale"
                    style={{ ['--tile' as string]: it.tile }}
                    onMouseEnter={() => setHovered(it.href)}
                    onMouseLeave={() => setHovered(null)}
                    title={it.disabledTitle || it.label}
                    aria-label={`${it.label} locked`}
                  >
                    {it.icon}
                  </button>
                ) : (
                  <Link
                    href={it.href}
                    className={`nav-mini ${active ? 'active' : ''}`}
                    style={{ ['--tile' as string]: it.tile }}
                    onMouseEnter={() => setHovered(it.href)}
                    onMouseLeave={() => setHovered(null)}
                    title={it.label}
                  >
                    {it.icon}
                  </Link>
                )}
                {hovered === it.href && (
                  <div className="absolute left-[calc(100%+8px)] top-1/2 -translate-y-1/2 z-50 pointer-events-none bg-gray-900 text-white text-xs px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap">
                    {it.disabled ? `${it.label} locked` : it.label}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="side-bottom-mini">
          <button
            type="button"
            onClick={handleLogout}
            className="mini-avatar"
            title="Sign out"
            aria-label={`Sign out (${userLabel})`}
          >
            {avatarLetter}
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside className={`ics-side ics-side-full ${className}`}>
      <div className="side-top">
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <Image src="/logo.png" alt="" width={26} height={26} className="rounded flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="brand-name font-serif text-[17px] leading-none text-gray-900">India Case Status</div>
            <div className="brand-sub mono text-[9.5px] tracking-[0.18em] uppercase text-gray-500 mt-1 truncate">
              Litigation workspace
            </div>
          </div>
        </div>
        <button
          type="button"
          className="icon-btn"
          onClick={onToggle}
          title="Collapse sidebar"
          aria-label="Collapse sidebar"
        >
          {IconCollapse}
        </button>
      </div>

      <button type="button" className="cmd-trigger" onClick={onOpenCmd}>
        {IconSearch}
        <span>Ask or jump to…</span>
        <span className="kbd mono">⌘K</span>
      </button>

      <div className="nav-section">
        <div className="nav-label eyebrow">Workspace</div>
        {workspaceItems.map((it) => {
          const active = isActive(it);
          if (it.disabled) {
            return (
              <button
                key={it.href}
                type="button"
                disabled
                title={it.disabledTitle || `${it.label} locked`}
                className="nav-item cursor-not-allowed opacity-50"
              >
                <span className="text-gray-500">{it.icon}</span>
                <span>{it.label}</span>
              </button>
            );
          }

          return (
            <Link
              key={it.href}
              href={it.href}
              className={`nav-item ${active ? 'active' : ''}`}
            >
              <span className="text-gray-500">{it.icon}</span>
              <span>{it.label}</span>
            </Link>
          );
        })}
      </div>

      <div className="nav-section">
        <div className="nav-label eyebrow">{workspaceNav.sectionLabel}</div>
        {accountItems.map((it) => {
          const active = isActive(it);
          return (
            <Link
              key={it.href}
              href={it.href}
              className={`nav-item ${active ? 'active' : ''}`}
            >
              <span className="text-gray-500">{it.icon}</span>
              <span>{it.label}</span>
            </Link>
          );
        })}
      </div>

      <div className="side-bottom">
        <div className="avatar">{avatarLetter}</div>
        <div className="flex-1 min-w-0">
          <div className="user-name truncate">{userLabel}</div>
          <div className="user-role mono text-[10px] tracking-wide uppercase text-gray-500">{workspaceNav.roleLabel}</div>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="icon-btn"
          title="Sign out"
          aria-label="Sign out"
        >
          {IconLogout}
        </button>
      </div>
    </aside>
  );
}
